import { useState, useCallback } from 'react';
import { ReportesService, type ReporteEnMemoria, type ReporteCreateResponse } from '../services/reportesService';
import { guardarBorrador, limpiarBorrador } from '../utils/reporteDraftStorage';

export interface EstadoEnvio {
  enviando: boolean;
  progreso: number;
  pasoActual: string;
  completado: boolean;
  error: string | null;
  reporteCreado: ReporteCreateResponse['data'] | null;
}

export interface UseEnviarReporteReturn {
  estado: EstadoEnvio;
  enviarReporte: (reporte: ReporteEnMemoria, empresaId: number) => Promise<void>;
  limpiarEstado: () => void;
  reintentar: () => Promise<void>;
}

const estadoInicial: EstadoEnvio = {
  enviando: false,
  progreso: 0,
  pasoActual: '',
  completado: false,
  error: null,
  reporteCreado: null,
};

/**
 * Hook personalizado para manejar el envío atómico de reportes
 * Proporciona estado de loading, progreso y manejo de errores
 */
export const useEnviarReporte = (): UseEnviarReporteReturn => {
  const [estado, setEstado] = useState<EstadoEnvio>(estadoInicial);
  const [ultimoReporte, setUltimoReporte] = useState<ReporteEnMemoria | null>(null);
  const [ultimaEmpresaId, setUltimaEmpresaId] = useState<number | null>(null);

  const enviarReporte = useCallback(async (reporte: ReporteEnMemoria, empresaId: number) => {
    try {
      // Guardar para posible reintento (en memoria, para el botón "Reintentar")
      setUltimoReporte(reporte);
      setUltimaEmpresaId(empresaId);

      // Borrador en el celular: si la app se cierra o pierde cobertura antes
      // de que el servidor confirme, esto sobrevive para poder recuperarlo
      guardarBorrador(reporte, empresaId);

      // Resetear estado e iniciar envío
      setEstado({
        ...estadoInicial,
        enviando: true,
        pasoActual: 'Iniciando envío...',
      });
      // Callback de progreso
      const onProgress = (paso: string, progreso: number) => {
        setEstado(prev => ({
          ...prev,
          progreso,
          pasoActual: paso,
        }));
      };

      // Operación atómica
      const resultado = await ReportesService.crearReporteCompleto(
        reporte,
        empresaId,
        onProgress
      );

      // Éxito — ya está confirmado por el servidor, se puede borrar el borrador local
      limpiarBorrador();
      setEstado({
        enviando: false,
        progreso: 100,
        pasoActual: 'Reporte enviado exitosamente',
        completado: true,
        error: null,
        reporteCreado: resultado.data,
      });
    } catch (error: any) {
      console.error('❌ Error al enviar reporte:', error);
      
      const mensajeError = error?.message || 'Error desconocido al enviar reporte';
      
      setEstado({
        enviando: false,
        progreso: 0,
        pasoActual: '',
        completado: false,
        error: mensajeError,
        reporteCreado: null,
      });
    }
  }, []);

  const reintentar = useCallback(async () => {
    if (ultimoReporte && ultimaEmpresaId) {
      await enviarReporte(ultimoReporte, ultimaEmpresaId);
    }
  }, [ultimoReporte, ultimaEmpresaId, enviarReporte]);

  const limpiarEstado = useCallback(() => {
    setEstado(estadoInicial);
    setUltimoReporte(null);
    setUltimaEmpresaId(null);
  }, []);

  return {
    estado,
    enviarReporte,
    limpiarEstado,
    reintentar,
  };
};

export default useEnviarReporte;
